import { useQuery } from '@tanstack/react-query';
import { Alert, Card, Col, Flex, Row, Skeleton, Typography } from 'antd';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EChart, type EChartClick, type EChartOption } from '../../components/EChart';
import { ApiError } from '../../lib/api-client';
import { formatDateTime } from '../../lib/format';
import { formatMoney } from '../../lib/money';
import { STREAMS, fetchPurchasesSummary, type PurchaseLinesFilter, type Stream } from './api';
import { unitsTooltipFormatter } from './chart-tooltip';
import { formatUnits, monthName, plotValue, sumMoney, sumUnits, toSen } from './figures';
import { PurchaseLinesDrawer } from './PurchaseLinesDrawer';
import { STREAM_COLOUR } from './SalesTab';
import { Tile } from './Tile';

const { Text } = Typography;

interface Drill {
  filter: PurchaseLinesFilter;
  title: string;
}

/** Insights, Purchasing: what NKS bought from suppliers, by month and stream, and from whom. */
export function PurchasingTab({ year }: { year: number }): ReactNode {
  const { t, i18n } = useTranslation();
  const [drill, setDrill] = useState<Drill | null>(null);
  const language = i18n.language;

  const summary = useQuery({
    queryKey: ['analytics', 'purchases', 'summary', year],
    queryFn: () => fetchPurchasesSummary(year),
  });

  const streamLabel = (s: Stream): string =>
    s === 'UNCLASSIFIED' ? t('insights.purchasing.otherPurchases') : t(`insights.stream.${s}`);

  const yearMonths = useMemo(
    () => (summary.data?.months ?? []).filter((m) => m.year === year),
    [summary.data, year],
  );
  // Only streams that bought something this year, so the legend stays short.
  const streams = useMemo(
    () => STREAMS.filter((s) => yearMonths.some((m) => toSen(m.byStream[s]) !== 0n)),
    [yearMonths],
  );

  const monthOption = useMemo((): EChartOption => {
    const compact = new Intl.NumberFormat(language, { notation: 'compact', maximumFractionDigits: 1 });
    const axisText = { color: 'var(--ink-muted)' };
    const label = (s: Stream): string =>
      s === 'UNCLASSIFIED' ? t('insights.purchasing.otherPurchases') : t(`insights.stream.${s}`);
    return {
      grid: { left: 8, right: 8, top: 16, bottom: 8, containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: unitsTooltipFormatter(
          (id, index) => {
            const m = yearMonths[index];
            const stream = STREAMS.find((s) => s === id);
            return m === undefined || stream === undefined ? undefined : formatMoney(m.byStream[stream]);
          },
          (index) => `${monthName(index + 1, language, 'long')} ${String(year)}`,
        ),
        backgroundColor: 'var(--surface-raised)',
        borderColor: 'var(--line)',
        textStyle: { color: 'var(--ink)' },
        confine: true,
      },
      xAxis: {
        type: 'category',
        data: yearMonths.map((m) => monthName(m.month, language)),
        axisLabel: axisText,
        axisLine: { lineStyle: { color: 'var(--line-strong)' } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { ...axisText, formatter: (v: number) => compact.format(v) },
        splitLine: { lineStyle: { color: 'var(--line)' } },
      },
      series: streams.map((stream) => ({
        id: stream,
        name: label(stream),
        type: 'bar',
        stack: 'purchases',
        barMaxWidth: 32,
        cursor: 'pointer',
        itemStyle: { color: STREAM_COLOUR[stream], borderColor: 'var(--surface)', borderWidth: 1 },
        emphasis: { focus: 'series' },
        data: yearMonths.map((m) => plotValue(m.byStream[stream])),
      })),
    };
  }, [yearMonths, streams, language, year, t]);

  const supplierName = (s: { creditorCode: string | null; creditorName: string | null }): string =>
    s.creditorName ?? s.creditorCode ?? t('insights.purchasing.noSupplier');

  const supplierOption = useMemo((): EChartOption => {
    const axisText = { color: 'var(--ink-muted)' };
    // A horizontal bar chart draws its first category at the bottom, so the
    // largest supplier goes last to sit on top.
    const ordered = [...(summary.data?.topSuppliers ?? [])].reverse();
    return {
      grid: { left: 8, right: 24, top: 8, bottom: 8, containLabel: true },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: unitsTooltipFormatter(
          (_id, index) => {
            const s = ordered[index];
            return s === undefined ? undefined : formatMoney(s.amount);
          },
          (index) => {
            const s = ordered[index];
            return s === undefined ? '' : supplierName(s);
          },
        ),
        backgroundColor: 'var(--surface-raised)',
        borderColor: 'var(--line)',
        textStyle: { color: 'var(--ink)' },
        confine: true,
      },
      xAxis: { type: 'value', axisLabel: axisText, splitLine: { lineStyle: { color: 'var(--line)' } } },
      yAxis: {
        type: 'category',
        data: ordered.map((s) => supplierName(s)),
        axisLabel: axisText,
        axisLine: { lineStyle: { color: 'var(--line-strong)' } },
        axisTick: { show: false },
      },
      series: [
        {
          id: 'AMOUNT',
          name: t('insights.purchasing.amount'),
          type: 'bar',
          barMaxWidth: 20,
          cursor: 'pointer',
          itemStyle: { color: 'var(--series-1)' },
          emphasis: { focus: 'series' },
          data: ordered.map((s) => plotValue(s.amount)),
        },
      ],
    };
  }, [summary.data, t]);

  if (summary.error !== null) {
    const denied = summary.error instanceof ApiError && summary.error.status === 403;
    return <Alert type="error" showIcon message={t(denied ? 'insights.noAccess' : 'insights.loadFailed')} />;
  }
  if (summary.data === undefined) return <Skeleton active paragraph={{ rows: 8 }} />;

  const data = summary.data;
  if (data.dataAsOf === null) {
    return <Alert type="info" showIcon message={t('insights.purchasing.nothingYet')} />;
  }

  const lastYear = String(year - 1);
  const through = data.comparedThroughMonth;
  const range =
    through === 1
      ? t('insights.sales.vsMonth', { from: monthName(1, language), year: lastYear })
      : t('insights.sales.vsRange', { from: monthName(1, language), to: monthName(through, language), year: lastYear });

  const bikesBought = sumUnits(data.bikesBought.map((b) => b.units));
  const bikesSold = sumUnits(data.bikesSold.map((b) => b.units));
  const partsBought = sumMoney(data.partsBought.map((p) => p.amount));
  const partsCostSold = sumMoney(data.partsCostSold.map((p) => p.cost));

  const openMonth = (click: EChartClick): void => {
    const m = yearMonths[click.dataIndex];
    if (m === undefined) return;
    const stream = STREAMS.find((s) => s === click.seriesId);
    if (stream === undefined) {
      setDrill({
        filter: { year, month: m.month },
        title: t('insights.purchasing.titleMonth', { month: monthName(m.month, language, 'long'), year: String(year) }),
      });
      return;
    }
    setDrill({
      filter: { year, month: m.month, stream },
      title: t('insights.lines.titleMonth', {
        stream: streamLabel(stream),
        month: monthName(m.month, language, 'long'),
        year: String(year),
      }),
    });
  };

  const openSupplier = (click: EChartClick): void => {
    const ordered = [...data.topSuppliers].reverse();
    const supplier = ordered[click.dataIndex];
    if (supplier?.creditorCode == null) return;
    setDrill({
      filter: { year, creditorCode: supplier.creditorCode },
      title: t('insights.purchasing.titleSupplier', { supplier: supplierName(supplier), year: String(year) }),
    });
  };

  return (
    <Flex vertical gap={16}>
      <Text type="secondary">{t('insights.dataAsOf', { at: formatDateTime(data.dataAsOf) })}</Text>

      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12} xl={8}>
          <Tile id="purchases" label={t('insights.purchasing.thisYear')} value={formatMoney(data.thisYear)}>
            <Text type="secondary">
              {t('insights.purchasing.vsLastYear', { range, amount: formatMoney(data.sameMonthsLastYear) })}
            </Text>
          </Tile>
        </Col>
        <Col xs={24} sm={12} xl={8}>
          <Tile id="bikes-bought" label={t('insights.purchasing.bikesBought')} value={formatUnits(bikesBought)}>
            <Text type="secondary">{t('insights.purchasing.bikesSold', { units: formatUnits(bikesSold) })}</Text>
          </Tile>
        </Col>
        <Col xs={24} sm={12} xl={8}>
          <Tile id="parts-bought" label={t('insights.purchasing.partsBought')} value={formatMoney(partsBought)}>
            <Text type="secondary">
              {t('insights.purchasing.partsCostSold', { amount: formatMoney(partsCostSold) })}
            </Text>
          </Tile>
        </Col>
      </Row>

      <Card size="small" title={t('insights.purchasing.chartTitle')}>
        <Flex vertical gap={8}>
          <Text type="secondary">{t('insights.purchasing.chartHelp')}</Text>
          <Flex gap={12} wrap component="ul" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {streams.map((stream) => (
              <Flex key={stream} component="li" gap={6} align="center">
                <span aria-hidden style={{ width: 12, height: 12, borderRadius: 3, background: STREAM_COLOUR[stream] }} />
                <Text>{streamLabel(stream)}</Text>
              </Flex>
            ))}
          </Flex>
          <EChart option={monthOption} height={340} onClick={openMonth} />
        </Flex>
      </Card>

      <Card size="small" title={t('insights.purchasing.suppliersTitle')}>
        <Flex vertical gap={8}>
          <Text type="secondary">{t('insights.purchasing.suppliersHelp')}</Text>
          {data.topSuppliers.length === 0 ? (
            <Text type="secondary">{t('insights.purchasing.noSuppliers')}</Text>
          ) : (
            <EChart option={supplierOption} height={Math.max(200, data.topSuppliers.length * 28 + 24)} onClick={openSupplier} />
          )}
        </Flex>
      </Card>

      <PurchaseLinesDrawer
        filter={drill?.filter ?? null}
        title={drill?.title ?? ''}
        onClose={() => {
          setDrill(null);
        }}
      />
    </Flex>
  );
}
